import React from 'react';
import { products } from '../utils/mockData';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const ProductsSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="py-24 bg-gray-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-bharat-blue rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-manufacturing-orange rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-manufacturing-orange/10 text-manufacturing-orange px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Our Product Portfolio
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-bharat-blue mb-6">
            Premium Titanium Dioxide Solutions
          </h2>
          <p className="text-xl text-steel-grey max-w-3xl mx-auto">
            High-quality TiO₂ grades tailored to industrial manufacturing needs
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {products.map((product, index) => (
            <div 
              key={product.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
            >
              {/* Product Image */}
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bharat-blue/80 to-transparent"></div>
                <div className="absolute top-4 right-4">
                  <div className="bg-manufacturing-orange text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {product.type}
                  </div>
                </div>
              </div>

              {/* Product Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-bharat-blue mb-3">
                  {product.name}
                </h3>
                
                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-steel-grey uppercase mb-3">Key Features</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-industrial-green flex-shrink-0 mt-0.5" />
                        <span className="text-steel-grey">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Applications */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="text-sm font-semibold text-steel-grey uppercase mb-2">Ideal For</div>
                  <div className="text-bharat-blue">{product.applications}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Supply Model Features */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
          <h3 className="text-2xl font-bold text-bharat-blue mb-8 text-center">
            Our Supply Model Ensures
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'Stable Availability', desc: 'Consistent stock levels' },
              { title: 'Competitive Pricing', desc: 'Market-leading rates' },
              { title: 'Structured Credit', desc: '30-45 day cycles' },
              { title: 'Quality Standards', desc: 'Consistent grade quality' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-manufacturing-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-manufacturing-orange" />
                </div>
                <h4 className="font-semibold text-bharat-blue mb-2">{item.title}</h4>
                <p className="text-steel-grey text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button 
            size="lg"
            onClick={() => scrollToSection('contact')}
            className="bg-manufacturing-orange hover:bg-manufacturing-orange/90 text-white text-lg px-8 py-6"
          >
            Request Product Specifications
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
